/**
 * File: tableExport.ts
 * Description: Dependency-free helpers to export tabular data to CSV, JSON
 *   and XLSX from the browser. The XLSX writer produces a genuine Open XML
 *   (.xlsx) package using a minimal store-only ZIP so no third-party library
 *   (and its supply-chain risk) is required.
 * Author: Development Team
 * Created: 2026-06-10
 * Last Modified: 2026-06-10
 */

const CSV_MIME = 'text/csv;charset=utf-8;';
const JSON_MIME = 'application/json;charset=utf-8;';
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const ZIP_LOCAL_SIG = 0x04034b50;
const ZIP_CENTRAL_SIG = 0x02014b50;
const ZIP_EOCD_SIG = 0x06054b50;
const ZIP_VERSION = 20;
const CRC32_POLYNOMIAL = 0xedb88320;
const BYTE_VALUES = 256;
const BITS_PER_BYTE = 8;
const CRC32_INIT = 0xffffffff;

/** A single export row: ordered cell values aligned to the headers. */
export type ExportRow = readonly string[];

/** A parsed markdown table: its column headers and aligned data rows. */
export interface ParsedTable {
  headers: string[];
  rows: string[][];
}

/**
 * Split a single markdown table row into its trimmed cell values.
 *
 * Handles the leading/trailing pipe and escaped pipes (``\|``) within cells.
 *
 * @param line - A markdown table row (e.g. ``| a | b | c |``).
 * @return The trimmed cell values, in column order.
 */
function splitMarkdownRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  // Split on unescaped pipes, then unescape any ``\|`` sequences.
  return trimmed
    .split(/(?<!\\)\|/)
    .map((cell) => cell.replace(/\\\|/g, '|').trim());
}

/**
 * Returns true when a markdown row is a header separator (e.g. ``|---|---|``).
 *
 * @param line - A markdown table row.
 * @return Whether the row only contains dashes, colons, pipes and spaces.
 */
function isSeparatorRow(line: string): boolean {
  const cells = splitMarkdownRow(line);
  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?-{1,}:?$/.test(cell.replace(/\s/g, '')))
  );
}

/**
 * Extract the first GitHub-flavoured markdown table found in the given text.
 *
 * A table is a run of consecutive lines that start with ``|`` containing a
 * header row, a separator row, and one or more data rows. Rows with a
 * different column count than the header are padded/truncated to align.
 *
 * @param markdown - Arbitrary markdown text (e.g. a generated section).
 * @return The parsed table, or null when no table is present.
 */
export function parseFirstMarkdownTable(markdown: string): ParsedTable | null {
  const lines = markdown.split(/\r?\n/);
  let start = -1;
  for (let i = 0; i < lines.length - 1; i += 1) {
    const current = lines[i].trim();
    const next = lines[i + 1].trim();
    if (current.startsWith('|') && next.startsWith('|') && isSeparatorRow(next)) {
      start = i;
      break;
    }
  }
  if (start === -1) {
    return null;
  }

  const headers = splitMarkdownRow(lines[start]);
  const rows: string[][] = [];
  for (let i = start + 2; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line.startsWith('|')) {
      break;
    }
    const cells = splitMarkdownRow(lines[i]);
    // Align each data row to the header width.
    const aligned = headers.map((_, col) => cells[col] ?? '');
    rows.push(aligned);
  }

  return { headers, rows };
}

/**
 * Triggers a browser download for the given binary or text payload.
 *
 * @param filename - Suggested file name including extension.
 * @param data - The payload to download (string or binary).
 * @param mime - The MIME type for the blob.
 */
function triggerDownload(
  filename: string,
  data: string | Uint8Array,
  mime: string,
): void {
  const blob = new Blob([data as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Escapes a single value for safe inclusion in a CSV cell.
 *
 * @param value - The raw cell value.
 * @return The CSV-safe representation.
 */
function escapeCsvValue(value: string): string {
  const needsQuotes = /[",\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

/**
 * Exports the given headers and rows as a downloadable CSV file.
 *
 * @param filename - File name including the .csv extension.
 * @param headers - Column headers.
 * @param rows - Row data aligned to the headers.
 */
export function exportRowsAsCsv(
  filename: string,
  headers: readonly string[],
  rows: readonly ExportRow[],
): void {
  const lines = [headers, ...rows].map((row) =>
    row.map(escapeCsvValue).join(','),
  );
  // Prepend a BOM so Excel detects UTF-8 correctly.
  const content = `\uFEFF${lines.join('\r\n')}`;
  triggerDownload(filename, content, CSV_MIME);
}

/**
 * Exports an array of plain objects as a pretty-printed JSON file.
 *
 * @param filename - File name including the .json extension.
 * @param objects - The objects to serialise.
 */
export function exportRowsAsJson(
  filename: string,
  objects: readonly Record<string, string>[],
): void {
  const content = JSON.stringify(objects, null, 2);
  triggerDownload(filename, content, JSON_MIME);
}

/** Builds the lookup table used by the CRC32 implementation. */
function buildCrc32Table(): Uint32Array {
  const table = new Uint32Array(BYTE_VALUES);
  for (let n = 0; n < BYTE_VALUES; n += 1) {
    let c = n;
    for (let k = 0; k < BITS_PER_BYTE; k += 1) {
      c = c & 1 ? CRC32_POLYNOMIAL ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
}

const CRC32_TABLE = buildCrc32Table();

/**
 * Computes the CRC32 checksum of the given bytes.
 *
 * @param bytes - The data to checksum.
 * @return The unsigned 32-bit CRC.
 */
function crc32(bytes: Uint8Array): number {
  let crc = CRC32_INIT;
  for (let i = 0; i < bytes.length; i += 1) {
    const index = (crc ^ bytes[i]) & 0xff;
    crc = CRC32_TABLE[index] ^ (crc >>> BITS_PER_BYTE);
  }
  return (crc ^ CRC32_INIT) >>> 0;
}

/** A single file entry to be packed into the ZIP archive. */
interface ZipEntry {
  name: string;
  data: Uint8Array;
  crc: number;
}

const TEXT_ENCODER = new TextEncoder();

/**
 * Escapes a value for safe inclusion inside XML text content.
 *
 * @param value - The raw text value.
 * @return The XML-escaped representation.
 */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Standard XML prolog shared by every OOXML part written by this module. */
const XML_DECLARATION =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

const CONTENT_TYPES_XML =
  `${XML_DECLARATION}<Types xmlns="http://schemas.openxmlformats.org/package/2006/` +
  'content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxml' +
  'formats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/' +
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType=' +
  '"application/vnd.openxmlformats-officedocument.spreadsheetml.' +
  'worksheet+xml"/>' +
  '</Types>';

const ROOT_RELS_XML =
  `${XML_DECLARATION}<Relationships xmlns="http://schemas.openxmlformats.org/package/` +
  '2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/' +
  'officeDocument/2006/relationships/officeDocument" ' +
  'Target="xl/workbook.xml"/>' +
  '</Relationships>';

const WORKBOOK_RELS_XML =
  `${XML_DECLARATION}<Relationships xmlns="http://schemas.openxmlformats.org/package/` +
  '2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/' +
  'officeDocument/2006/relationships/worksheet" ' +
  'Target="worksheets/sheet1.xml"/>' +
  '</Relationships>';

// Excel sheet names are limited to 31 chars and cannot contain : \ / ? * [ ].
const SHEET_NAME_MAX_LENGTH = 31;
const DEFAULT_SHEET_NAME = 'Sheet1';

/**
 * Sanitise a worksheet name to satisfy Excel's naming constraints.
 *
 * @param name - The desired sheet name.
 * @return A safe, non-empty sheet name no longer than 31 characters.
 */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[:\\/?*[\]]/g, ' ').trim();
  return cleaned.slice(0, SHEET_NAME_MAX_LENGTH) || DEFAULT_SHEET_NAME;
}

/**
 * Build the workbook XML referencing a single worksheet with the given name.
 *
 * @param sheetName - The (already sanitised) worksheet display name.
 * @return The workbook XML string.
 */
function buildWorkbookXml(sheetName: string): string {
  return (
    `${XML_DECLARATION}<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/` +
    '2006/main" xmlns:r="http://schemas.openxmlformats.org/' +
    'officeDocument/2006/relationships">' +
    `<sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" ` +
    'r:id="rId1"/></sheets>' +
    '</workbook>'
  );
}

/**
 * Builds a single worksheet row as OOXML using inline strings.
 *
 * @param cells - The cell values for the row.
 * @param rowIndex - The 1-based row index.
 * @return The XML for the row.
 */
function buildSheetRow(cells: readonly string[], rowIndex: number): string {
  const cellXml = cells
    .map(
      (value) =>
        '<c t="inlineStr"><is><t xml:space="preserve">' +
        `${escapeXml(value)}</t></is></c>`,
    )
    .join('');
  return `<row r="${rowIndex}">${cellXml}</row>`;
}

/**
 * Builds the worksheet XML for the given headers and rows.
 *
 * @param headers - Column headers.
 * @param rows - Row data aligned to the headers.
 * @return The worksheet XML string.
 */
function buildSheetXml(
  headers: readonly string[],
  rows: readonly ExportRow[],
): string {
  const allRows = [headers, ...rows];
  const rowsXml = allRows
    .map((cells, index) => buildSheetRow(cells, index + 1))
    .join('');
  return (
    `${XML_DECLARATION}<worksheet xmlns="http://schemas.openxmlformats.org/` +
    'spreadsheetml/2006/main">' +
    `<sheetData>${rowsXml}</sheetData>` +
    '</worksheet>'
  );
}

/**
 * Creates a ZIP entry (stored, no compression) from a text part.
 *
 * @param name - The archive path of the entry.
 * @param content - The UTF-8 text content.
 * @return The prepared ZIP entry.
 */
function makeEntry(name: string, content: string): ZipEntry {
  const data = TEXT_ENCODER.encode(content);
  return { name, data, crc: crc32(data) };
}

/** Writes a 16-bit little-endian value into the view at the offset. */
function writeUint16(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value, true);
}

/** Writes a 32-bit little-endian value into the view at the offset. */
function writeUint32(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, true);
}

const LOCAL_HEADER_SIZE = 30;
const CENTRAL_HEADER_SIZE = 46;
const EOCD_SIZE = 22;

/**
 * Builds the local file header for a ZIP entry.
 *
 * @param entry - The entry to describe.
 * @param nameBytes - The UTF-8 encoded entry name.
 * @return The header bytes.
 */
function buildLocalHeader(entry: ZipEntry, nameBytes: Uint8Array): Uint8Array {
  const header = new Uint8Array(LOCAL_HEADER_SIZE + nameBytes.length);
  const view = new DataView(header.buffer);
  writeUint32(view, 0, ZIP_LOCAL_SIG);
  writeUint16(view, 4, ZIP_VERSION);
  writeUint32(view, 14, entry.crc);
  writeUint32(view, 18, entry.data.length);
  writeUint32(view, 22, entry.data.length);
  writeUint16(view, 26, nameBytes.length);
  header.set(nameBytes, LOCAL_HEADER_SIZE);
  return header;
}

/**
 * Builds a central directory record for a ZIP entry.
 *
 * @param entry - The entry to describe.
 * @param nameBytes - The UTF-8 encoded entry name.
 * @param offset - The byte offset of the entry's local header.
 * @return The central directory record bytes.
 */
function buildCentralRecord(
  entry: ZipEntry,
  nameBytes: Uint8Array,
  offset: number,
): Uint8Array {
  const record = new Uint8Array(CENTRAL_HEADER_SIZE + nameBytes.length);
  const view = new DataView(record.buffer);
  writeUint32(view, 0, ZIP_CENTRAL_SIG);
  writeUint16(view, 4, ZIP_VERSION);
  writeUint16(view, 6, ZIP_VERSION);
  writeUint32(view, 16, entry.crc);
  writeUint32(view, 20, entry.data.length);
  writeUint32(view, 24, entry.data.length);
  writeUint16(view, 28, nameBytes.length);
  writeUint32(view, 42, offset);
  record.set(nameBytes, CENTRAL_HEADER_SIZE);
  return record;
}

/** Concatenates a list of byte arrays into a single Uint8Array. */
function concatBytes(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

/**
 * Packs the given entries into a store-only ZIP archive.
 *
 * @param entries - The files to pack.
 * @return The complete ZIP archive bytes.
 */
function buildZip(entries: readonly ZipEntry[]): Uint8Array {
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;
  for (const entry of entries) {
    const nameBytes = TEXT_ENCODER.encode(entry.name);
    const localHeader = buildLocalHeader(entry, nameBytes);
    localParts.push(localHeader, entry.data);
    centralParts.push(buildCentralRecord(entry, nameBytes, offset));
    offset += localHeader.length + entry.data.length;
  }
  const centralDir = concatBytes(centralParts);
  const eocd = new Uint8Array(EOCD_SIZE);
  const view = new DataView(eocd.buffer);
  writeUint32(view, 0, ZIP_EOCD_SIG);
  writeUint16(view, 8, entries.length);
  writeUint16(view, 10, entries.length);
  writeUint32(view, 12, centralDir.length);
  writeUint32(view, 16, offset);
  return concatBytes([...localParts, centralDir, eocd]);
}

/**
 * Exports the given headers and rows as a genuine .xlsx workbook.
 *
 * @param filename - File name including the .xlsx extension.
 * @param headers - Column headers.
 * @param rows - Row data aligned to the headers.
 * @param sheetName - Optional worksheet display name (default "Sheet1").
 */
export function exportRowsAsXlsx(
  filename: string,
  headers: readonly string[],
  rows: readonly ExportRow[],
  sheetName: string = DEFAULT_SHEET_NAME,
): void {
  const safeSheetName = sanitizeSheetName(sheetName);
  const entries: ZipEntry[] = [
    makeEntry('[Content_Types].xml', CONTENT_TYPES_XML),
    makeEntry('_rels/.rels', ROOT_RELS_XML),
    makeEntry('xl/workbook.xml', buildWorkbookXml(safeSheetName)),
    makeEntry('xl/_rels/workbook.xml.rels', WORKBOOK_RELS_XML),
    makeEntry('xl/worksheets/sheet1.xml', buildSheetXml(headers, rows)),
  ];
  triggerDownload(filename, buildZip(entries), XLSX_MIME);
}
